import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { Component, HostBinding, Input } from "@angular/core";
import { ClubService } from "../../providers/club.service";

import { BvIconComponent } from "../../design-system/primitives/icon.component";
import { LockedFeatureDialogComponent } from "./locked-feature-dialog.component";

/**
 * Wraps any control to make paid features visible-but-locked for free users.
 *
 *   <app-feature-lock feature="LEAGUES" tier="club" [subLevel]="30">
 *     <button bv-button variant="primary" (click)="createLeague()">Create league</button>
 *   </app-feature-lock>
 *
 * Gates on the active club's subLevel (the owner's tier), not the logged-in
 * user's — so staff in a paid club aren't blocked by their own free profile.
 *
 * - `feature`: short key identifying the feature (LEAGUES, BOUNTY, LOGO,
 *   BACKGROUND, CHIP_DISTRIBUTION, PLAYER_LIMIT, PRINT_TICKETS). Drives the
 *   dialog copy via the LOCKED_<FEATURE>_BODY translation key.
 * - `tier`: translation key for the required tier (e.g. "CLUB", "HOME_GAME",
 *   "PRO") — interpolated into the dialog title.
 * - `subLevel`: numeric subLevel required (e.g., 30 for Club, 20 for Home
 *   Game, 40 for Pro). Compared against `club.subLevel`.
 */
@Component({
    selector: "app-feature-lock",
    templateUrl: "./feature-lock.component.html",
    styleUrls: ["./feature-lock.component.scss"],
    imports: [BvIconComponent, LockedFeatureDialogComponent]
})
export class FeatureLockComponent {
  @Input() feature!: string;
  @Input() tier!: string;
  @Input() subLevel = 20;
  // Opt-in: make the wrapper a full-width block instead of the default
  // shrink-to-fit inline-block. Use when wrapping a full-width row (e.g. a
  // form field) so the projected control isn't squished.
  @Input() block = false;
  @HostBinding("class.feature-lock-block") get blockClass(): boolean {
    return this.block;
  }
  // Opt-in: suppress the corner lock badge while keeping the gating (shield +
  // upsell dialog). Use where the badge would clutter an inline control that
  // already reads as a paid action.
  @Input()
  get hideBadge(): boolean {
    return this._hideBadge;
  }
  set hideBadge(value: any) {
    this._hideBadge = coerceBooleanProperty(value);
  }
  private _hideBadge = false;

  lockedDialogOpen = false;

  constructor(public clubServ: ClubService) {}

  get locked(): boolean {
    return !this.clubServ.minClubLevel(this.subLevel);
  }

  onLockedClick(event: MouseEvent): void {
    if (!this.locked) return;
    event.stopPropagation();
    event.preventDefault();
    this.lockedDialogOpen = true;
  }
}
